import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseclient";
import { useNavigate } from "react-router-dom";
import ApplicationConfirmationModal from "../components/ApplicationConfirmationModal";
import DailyLimitCelebrationModal from "../components/DailyLimitCelebrationModal";
import MatchAnalysisModal from "../components/MatchAnalysisModal";
import API_URL from "../config/api";
import IconImage from "../components/IconImage";

const DAILY_APPLICATION_CAP = 5;

/** Motivational line for the persistent application tracker (by slots remaining today). */
function getDailyApplyMotivation(remaining) {
  if (remaining >= DAILY_APPLICATION_CAP) return "Let's make today special!";
  if (remaining === 4) return "First one down—keep it going!";
  if (remaining === 3) return "Halfway there—you're crushing it!";
  if (remaining === 2) return "Two left—keep the momentum up!";
  if (remaining === 1) return "Last one—make it count!";
  return `You used all ${DAILY_APPLICATION_CAP}—outstanding. See you tomorrow!`;
}

function JobList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("posted_date");
  const [selectedJob, setSelectedJob] = useState(null);
  const [user, setUser] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pendingJobApplication, setPendingJobApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showDailyLimitCelebration, setShowDailyLimitCelebration] =
    useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchAnalysis, setMatchAnalysis] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState(null);
  const [matchCache, setMatchCache] = useState({}); // Cache results by job ID
  const [jobSummaryCache, setJobSummaryCache] = useState({}); // AI formatted job posting JSON
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [applicationLimit, setApplicationLimit] = useState(null); // Store limit info: { can_apply, applications_today, remaining }
  const [savedJobIds, setSavedJobIds] = useState({});
  const [saveJobBusyId, setSaveJobBusyId] = useState(null);
  const windowBlurTime = useRef(null);
  const navigate = useNavigate();

  // Check application limit for the current user
  const checkApplicationLimit = useCallback(async (userId) => {
    if (!userId) return;

    try {
      const { data, error } = await supabase.rpc("check_application_limit", {
        p_user_id: userId,
      });

      if (error) {
        console.error("Error checking application limit:", error);
        // On error, allow application (fail open)
        setApplicationLimit({
          can_apply: true,
          applications_today: 0,
          remaining: DAILY_APPLICATION_CAP,
        });
      } else {
        // Supabase TABLE-returning RPCs return an array of rows; use first row
        const row = Array.isArray(data) ? data[0] : data;
        setApplicationLimit(
          row
            ? {
                can_apply: row.can_apply !== false,
                applications_today: row.applications_today ?? 0,
                remaining: row.remaining ?? DAILY_APPLICATION_CAP,
              }
            : {
                can_apply: true,
                applications_today: 0,
                remaining: DAILY_APPLICATION_CAP,
              },
        );
      }
    } catch (err) {
      console.error("Error checking application limit:", err);
      // On error, allow application (fail open)
      setApplicationLimit({
        can_apply: true,
        applications_today: 0,
        remaining: DAILY_APPLICATION_CAP,
      });
    }
  }, []);

  const fetchSavedJobs = useCallback(async (userId) => {
    if (!userId) {
      setSavedJobIds({});
      return;
    }
    const { data, error } = await supabase
      .from("saved_jobs")
      .select("job_id")
      .eq("user_id", userId);

    if (error) {
      console.error("Error fetching saved jobs:", error);
      return;
    }
    const next = {};
    (data || []).forEach((row) => {
      next[row.job_id] = true;
    });
    setSavedJobIds(next);
  }, []);

  useEffect(() => {
    fetchSavedJobs(user?.id);
  }, [user?.id, fetchSavedJobs]);

  // Check if user is authenticated
  const checkUser = useCallback(async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const currentUser = session?.user || null;
      setUser(currentUser);
      if (currentUser) {
        checkApplicationLimit(currentUser.id);
      }
    } catch (error) {
      console.error("Error checking user:", error);
    }
  }, [checkApplicationLimit]);

  // Fetch jobs from database and check user authentication
  useEffect(() => {
    fetchJobs();

    // Check initial user session
    checkUser();

    // Add window resize listener
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add auth state listener - this handles all user state changes (login, logout, switch account)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      // Always clear limit when auth changes so we never show the previous user's limit
      setApplicationLimit(null);
      if (!currentUser) {
        setShowDailyLimitCelebration(false);
        setSavedJobIds({});
      }
      if (currentUser) {
        checkApplicationLimit(currentUser.id);
      }
    });

    // Track window blur (when user navigates away) and focus (when they return)
    const handleBlur = () => {
      // User navigated away - record the time
      if (pendingJobApplication) {
        windowBlurTime.current = Date.now();
      }
    };

    const handleFocus = () => {
      // User returned - check if they were away for at least 2 seconds
      // This helps avoid showing popup if they just clicked elsewhere on the page
      if (pendingJobApplication && windowBlurTime.current) {
        const timeAway = Date.now() - windowBlurTime.current;
        if (timeAway > 2000) {
          // Show popup after 2 seconds away
          setShowApplicationModal(true);
        }
      }
    };

    // Also use visibility API for better detection
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden
        if (pendingJobApplication) {
          windowBlurTime.current = Date.now();
        }
      } else {
        // Page is visible again
        if (pendingJobApplication && windowBlurTime.current) {
          const timeAway = Date.now() - windowBlurTime.current;
          if (timeAway > 2000) {
            setShowApplicationModal(true);
          }
        }
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      subscription.unsubscribe();
    };
  }, [pendingJobApplication, checkUser, checkApplicationLimit]);

  // Handle logout
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out:", error);
      }
      // Always clear local state so UI updates (session may already be missing/expired)
      setUser(null);
      setShowUserMenu(false);
      setShowDailyLimitCelebration(false);
    } catch (error) {
      // AuthSessionMissingError etc. — treat as signed out and clear state
      console.error("Error during logout:", error);
      setUser(null);
      setShowUserMenu(false);
      setShowDailyLimitCelebration(false);
    }
  };

  // Handle user menu toggle
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Handle resume-job match analysis
  const handleAnalyzeMatch = async (job) => {
    if (!user) {
      navigate("/sign-in");
      return;
    }

    // Check if we have cached results for this job
    if (matchCache[job.id]) {
      setMatchAnalysis(matchCache[job.id]);
      setMatchError(null);
      setMatchLoading(false);
      setShowMatchModal(true);
      return;
    }

    setMatchLoading(true);
    setMatchError(null);
    setMatchAnalysis(null);
    setShowMatchModal(true);

    try {
      const response = await fetch(`${API_URL}/api/match/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          jobId: job.id,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to analyze match");
      }

      setMatchAnalysis(data);
      // Cache the result
      setMatchCache((prev) => ({ ...prev, [job.id]: data }));
    } catch (error) {
      console.error("Match analysis error:", error);
      setMatchError(error.message);
    } finally {
      setMatchLoading(false);
    }
  };

  const closeMatchModal = () => {
    setShowMatchModal(false);
  };

  // Get match color based on percentage
  const getMatchColor = (rate) => {
    if (rate >= 75) return "#28a745";
    if (rate >= 60) return "#ffc107";
    if (rate >= 40) return "#fd7e14";
    return "#dc3545";
  };

  // Fetch AI-generated job summary
  const fetchJobSummary = async (job) => {
    if (jobSummaryCache[job.id]) return; // Already cached

    setLoadingSummary(true);
    try {
      const rawText = [job.description, job.requirements]
        .filter(Boolean)
        .join("\n\n");

      const response = await fetch(`${API_URL}/api/match/job-posting`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobId: job.id,
          jobTitle: job.title,
          jobDescription: rawText, // Send full text, backend will handle truncation
          company: job.company,
          applicationUrl: job.application_url,
          source: job.source,
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      if (!response.ok) {
        const bodyText = await response.text();
        throw new Error(
          `HTTP ${response.status} from ${response.url}. content-type=${contentType}. Body starts with: ` +
            JSON.stringify(bodyText.slice(0, 200)),
        );
      }

      if (!contentType.includes("application/json")) {
        const bodyText = await response.text();
        throw new Error(
          `Expected JSON but got content-type=${contentType} from ${response.url}. Body starts with: ` +
            JSON.stringify(bodyText.slice(0, 200)),
        );
      }

      const data = await response.json();
      if (data.success) {
        setJobSummaryCache((prev) => ({ ...prev, [job.id]: data }));
      }
    } catch (error) {
      console.error("Failed to fetch formatted job posting:", error);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Handle clicking outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest(".user-menu-container")) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .eq("is_active", true)
        .eq("experience_level", "entry_level")
        .order("posted_date", { ascending: false });

      if (error) {
        throw error;
      }

      setJobs(data || []);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError("Failed to load jobs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort jobs
  const filteredJobs = jobs
    .filter((job) => {
      const matchesSearch =
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesLocation =
        locationFilter === "all" ||
        job.location.toLowerCase().includes(locationFilter.toLowerCase());

      return matchesSearch && matchesLocation;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "posted_date":
          return new Date(b.posted_date) - new Date(a.posted_date);
        case "salary_max":
          return (b.salary_max || 0) - (a.salary_max || 0);
        case "company":
          return a.company.localeCompare(b.company);
        case "title":
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  // Get unique locations for filter
  const uniqueLocations = [...new Set(jobs.map((job) => job.location))];

  // Only show limit-reached when we have a valid count (avoid "undefined/5" from stale or bad data)
  const isLimitReached =
    applicationLimit &&
    applicationLimit.can_apply === false &&
    typeof applicationLimit.applications_today === "number";

  const slotsRemaining =
    user && applicationLimit && typeof applicationLimit.remaining === "number"
      ? applicationLimit.remaining
      : null;
  const slotsUsed =
    user &&
    applicationLimit &&
    typeof applicationLimit.applications_today === "number"
      ? applicationLimit.applications_today
      : null;
  const slotsBarPct =
    slotsUsed != null
      ? Math.min(100, Math.max(0, (slotsUsed / DAILY_APPLICATION_CAP) * 100))
      : 0;

  // Format salary display (min/max are yearly in dollars; coerce to number for display)
  const formatSalary = (min, max, currency = "USD") => {
    const minNum = min != null ? Number(min) : NaN;
    const maxNum = max != null ? Number(max) : NaN;
    const hasMin = !Number.isNaN(minNum) && minNum > 0;
    const hasMax = !Number.isNaN(maxNum) && maxNum > 0;

    const symbol =
      (currency || "USD").toUpperCase() === "USD"
        ? "$"
        : (currency || "USD") + " ";
    const fmt = (n) => Math.round(n).toLocaleString();

    if (!hasMin && !hasMax) return "Salary not specified";
    if (hasMin && !hasMax) return `${symbol}${fmt(minNum)}+`;
    if (!hasMin && hasMax) return `Up to ${symbol}${fmt(maxNum)}`;
    if (hasMin && hasMax && minNum === maxNum) return `${symbol}${fmt(minNum)}`;
    return `${symbol}${fmt(minNum)} - ${symbol}${fmt(maxNum)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Today";
    if (diffDays === 2) return "Yesterday";
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <div style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>🔄</div>
        <div>Loading entry-level jobs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "red" }}>
        <div style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>❌</div>
        <div>{error}</div>
        <button
          onClick={fetchJobs}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Handle job selection
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    // Fetch AI summary when job is selected
    fetchJobSummary(job);
  };

  // Handle apply button click
  const handleApplyClick = (job, e) => {
    e.stopPropagation();

    if (!user) {
      // User not authenticated, redirect to sign-in
      navigate("/sign-in");
      return;
    }

    // Check application limit (only when we have valid limit data)
    if (isLimitReached) {
      const count = applicationLimit.applications_today;
      alert(
        `You have reached your daily limit of ${DAILY_APPLICATION_CAP} applications.\n\n` +
          `Applications today: ${count}\n` +
          `Please try again tomorrow.`,
      );
      return;
    }

    // User is authenticated, open application URL
    if (job.application_url) {
      // Store the job in state so we can show popup when they return
      setPendingJobApplication(job);
      windowBlurTime.current = null;
      window.open(job.application_url, "_blank");
    }
  };

  const handleToggleSave = async (job, e) => {
    e?.stopPropagation?.();
    if (!user) {
      navigate("/sign-in");
      return;
    }
    if (saveJobBusyId === job.id) return;

    const isSaved = !!savedJobIds[job.id];
    setSaveJobBusyId(job.id);
    try {
      if (isSaved) {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", user.id)
          .eq("job_id", job.id);
        if (error) throw error;
        setSavedJobIds((prev) => {
          const next = { ...prev };
          delete next[job.id];
          return next;
        });
      } else {
        const { error } = await supabase.from("saved_jobs").insert({
          user_id: user.id,
          job_id: job.id,
        });
        if (error) throw error;
        setSavedJobIds((prev) => ({ ...prev, [job.id]: true }));
      }
    } catch (err) {
      console.error("Error toggling saved job:", err);
      alert("Could not update saved jobs. Please try again.");
    } finally {
      setSaveJobBusyId(null);
    }
  };

  // Handle application confirmation
  const handleApplicationConfirm = async () => {
    if (!pendingJobApplication || !user) return;

    let openDailyCelebration = false;

    try {
      // Use the database function to check limit and insert atomically
      const { data, error } = await supabase.rpc(
        "check_and_insert_application",
        {
          p_user_id: user.id,
          p_job_id: pendingJobApplication.id,
        },
      );

      if (error) {
        console.error("Error saving application:", error);
        alert("Failed to save application. Please try again.");
        return;
      }

      // Supabase TABLE-returning RPCs return an array of rows; use first row
      const row = Array.isArray(data) ? data[0] : data;
      const success = row?.success === true;
      const limitReached = row?.limit_reached === true;
      const applicationsToday = row?.applications_today ?? 0;

      if (!success) {
        // Limit reached or other error
        if (limitReached) {
          alert(
            `You have reached your daily limit of ${DAILY_APPLICATION_CAP} applications.\n\n` +
              `Applications today: ${applicationsToday}\n` +
              `Please try again tomorrow.`,
          );
          setApplicationLimit({
            can_apply: false,
            applications_today: applicationsToday,
            remaining: 0,
          });
        } else {
          alert(
            row?.message || "Failed to save application. Please try again.",
          );
        }
      } else {
        // Success - application recorded (RPC doesn't return remaining, derive it)
        const remainingAfter = Math.max(
          0,
          DAILY_APPLICATION_CAP - applicationsToday,
        );
        setApplicationLimit({
          can_apply: remainingAfter > 0,
          applications_today: applicationsToday,
          remaining: remainingAfter,
        });
        if (remainingAfter === 0) {
          openDailyCelebration = true;
        }
      }
    } catch (err) {
      console.error("Error handling application:", err);
      alert("An error occurred. Please try again.");
    } finally {
      setPendingJobApplication(null);
      setShowApplicationModal(false);
      windowBlurTime.current = null;
      if (openDailyCelebration) {
        setShowDailyLimitCelebration(true);
      }
    }
  };

  // Handle application cancellation
  const handleApplicationCancel = () => {
    setPendingJobApplication(null);
    setShowApplicationModal(false);
    windowBlurTime.current = null;
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        backgroundColor: "#f8f9fa",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        flexDirection: windowWidth < 768 ? "column" : "row",
        position: "relative",
      }}
    >
      {/* User Menu */}
      {user && (
        <div
          className="user-menu-container"
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
          }}
        >
          {/* User Icon */}
          <button
            onClick={toggleUserMenu}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              boxShadow: "0 2px 8px rgba(0, 123, 255, 0.3)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#0056b3";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#007bff";
              e.target.style.transform = "scale(1)";
            }}
          >
            👤
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <div
              style={{
                position: "absolute",
                top: "50px",
                right: "0",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                border: "1px solid #e9ecef",
                minWidth: "160px",
                overflow: "hidden",
                animation: "fadeInDown 0.2s ease",
              }}
            >
              <div
                style={{
                  padding: "8px 0",
                }}
              >
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate("/profile");
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "none",
                    backgroundColor: "transparent",
                    color: "#2c3e50",
                    fontSize: "14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  <span>👤</span>
                  Profile
                </button>

                <div
                  style={{
                    height: "1px",
                    backgroundColor: "#e9ecef",
                    margin: "4px 0",
                  }}
                />

                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "none",
                    backgroundColor: "transparent",
                    color: "#dc3545",
                    fontSize: "14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  <span>🚪</span>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Landing Overlay - Shows when user is not authenticated */}
      {!user && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(1px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "3rem",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              maxWidth: "580px",
              width: "100%",
              textAlign: "center",
              border: "1px solid #e9ecef",
            }}
          >
            <div style={{ marginBottom: "2rem" }}>
              <div
                style={{
                  fontSize: "4rem",
                  marginBottom: "1rem",
                  background: "linear-gradient(135deg, #007bff, #0056b3)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                🚀
              </div>
              <h1
                style={{
                  color: "#2c3e50",
                  fontSize: "2rem",
                  fontWeight: "700",
                  marginBottom: "1rem",
                  lineHeight: "1.2",
                }}
              >
                Welcome to ChapterOneJobs
              </h1>
              <p
                style={{
                  color: "#7f8c8d",
                  fontSize: "1.1rem",
                  lineHeight: "1.5",
                  marginBottom: "0.5rem",
                }}
              >
                A job board designed for thoughtful first steps—not endless
                applying.
              </p>
              <p
                style={{
                  color: "#6c757d",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                  marginBottom: "1.25rem",
                }}
              >
                No experience required • Remote & California-based opportunities
              </p>

              <div
                style={{
                  textAlign: "left",
                  padding: "1.25rem 1.35rem",
                  backgroundColor: "#f0f7ff",
                  borderRadius: "12px",
                  borderLeft: "4px solid #007bff",
                  marginBottom: "0.25rem",
                }}
              >
                <p
                  style={{
                    color: "#1a4d80",
                    fontSize: "0.8rem",
                    fontWeight: "700",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    margin: "0 0 0.6rem 0",
                  }}
                >
                  Apply with Purpose
                </p>
                <p
                  style={{
                    color: "#2c3e50",
                    fontSize: "0.95rem",
                    lineHeight: "1.55",
                    margin: "0 0 0.75rem 0",
                  }}
                >
                  You can submit up to{" "}
                  <strong style={{ color: "#0056b3" }}>
                    five applications each day
                  </strong>
                  . This cap is intentional. It encourages you to apply with
                  intention, align your resume, and feel confident about your
                  application instead of throwing resumes into the void.
                </p>
                <p
                  style={{
                    color: "#495057",
                    fontSize: "0.9rem",
                    lineHeight: "1.55",
                    margin: 0,
                  }}
                >
                  Treat ChapterOneJobs like a daily practice—show up, pick your
                  best fits, then come back tomorrow. Small, steady habits build
                  the dedication and persistence required to land your first
                  role.
                </p>
              </div>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <button
                onClick={() => navigate("/sign-up")}
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "1rem 2rem",
                  borderRadius: "8px",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#0056b3";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 16px rgba(0, 123, 255, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#007bff";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 4px 12px rgba(0, 123, 255, 0.3)";
                }}
              >
                Create Free Account
              </button>

              <button
                onClick={() => navigate("/sign-in")}
                style={{
                  backgroundColor: "transparent",
                  color: "#007bff",
                  border: "2px solid #007bff",
                  padding: "0.875rem 2rem",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#007bff";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#007bff";
                }}
              >
                Sign In
              </button>
            </div>

            <div
              style={{
                marginTop: "2rem",
                padding: "1rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <p
                style={{
                  color: "#6c757d",
                  fontSize: "0.85rem",
                  margin: 0,
                  lineHeight: "1.45",
                }}
              >
                💡 <strong>Why sign up?</strong> Unlock applying (with the daily
                five-application rhythm), track where you have already reached
                out, save roles worth a second look, and hear about new listings
                that fit you.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Blurred Background Content */}
      <div
        style={{
          display: "flex",
          height: "100vh",
          width: "100%",
          backgroundColor: "#f8f9fa",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          flexDirection: windowWidth < 768 ? "column" : "row",
          filter: !user ? "blur(1px) brightness(0.9)" : "none",
          transition: "filter 0.3s ease",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left Column - Job Listings */}
        <div
          style={{
            width: windowWidth < 768 ? "100%" : "40%",
            height: windowWidth < 768 ? (selectedJob ? "40%" : "100%") : "100%",
            backgroundColor: "white",
            borderRight: windowWidth < 768 ? "none" : "1px solid #e9ecef",
            borderBottom:
              windowWidth < 768 && selectedJob ? "1px solid #e9ecef" : "none",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "1.5rem",
              borderBottom: "1px solid #e9ecef",
              backgroundColor: "white",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <img
                src="/images/icons/ChapterOneLogo.png"
                alt="ChapterOne Logo"
                style={{
                  height: "60px",
                  width: "auto",
                }}
              />
              <h1
                style={{
                  color: "#2c3e50",
                  margin: 0,
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Chapter One Jobs
              </h1>
            </div>
            <button
              type="button"
              onClick={() => navigate("/blog")}
              style={{
                background: "none",
                border: "none",
                color: "#007bff",
                fontSize: "0.9rem",
                fontWeight: "600",
                cursor: "pointer",
                padding: 0,
                marginBottom: "0.5rem",
              }}
            >
              Career tips &amp; guides →
            </button>

            {user && !applicationLimit && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "10px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#6c757d",
                    textAlign: "center",
                  }}
                >
                  Loading today&apos;s application slots…
                </p>
              </div>
            )}

            {user &&
              applicationLimit &&
              slotsRemaining != null &&
              slotsUsed != null && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "0.9rem 1rem",
                    borderRadius: "10px",
                    border:
                      slotsRemaining === 0
                        ? "1px solid #c3e6cb"
                        : "1px solid #b8daff",
                    backgroundColor:
                      slotsRemaining === 0 ? "#f4fff8" : "#f0f7ff",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "0.5rem",
                      marginBottom: "0.45rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "700",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: slotsRemaining === 0 ? "#155724" : "#1a4d80",
                      }}
                    >
                      Today&apos;s applications
                    </span>
                    <span
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: "700",
                        color: "#2c3e50",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {slotsRemaining} of {DAILY_APPLICATION_CAP} left
                    </span>
                  </div>
                  <div
                    style={{
                      height: "9px",
                      borderRadius: "6px",
                      backgroundColor: "#dee2e6",
                      overflow: "hidden",
                      marginBottom: "0.65rem",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${slotsBarPct}%`,
                        borderRadius: "6px",
                        backgroundColor:
                          slotsRemaining === 0 ? "#28a745" : "#007bff",
                        transition:
                          "width 0.35s ease, background-color 0.25s ease",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.88rem",
                      lineHeight: 1.45,
                      color: "#34495e",
                      fontWeight: "500",
                    }}
                  >
                    {getDailyApplyMotivation(slotsRemaining)}
                  </p>
                </div>
              )}
          </div>

          {/* Search and Filters */}
          <div
            style={{
              padding: "1rem 1.5rem",
              borderBottom: "1px solid #e9ecef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div style={{ marginBottom: "1rem" }}>
              <input
                type="text"
                placeholder="Search jobs, companies, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#007bff")}
                onBlur={(e) => (e.target.style.borderColor = "#ddd")}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0.5rem",
              }}
            >
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                style={{
                  padding: "0.5rem",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "0.85rem",
                  backgroundColor: "white",
                }}
              >
                <option value="all">All Locations</option>
                {uniqueLocations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: "0.5rem",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "0.85rem",
                  backgroundColor: "white",
                }}
              >
                <option value="posted_date">Newest First</option>
                <option value="salary_max">Highest Salary</option>
                <option value="company">Company Name</option>
                <option value="title">Job Title</option>
              </select>
            </div>

            <div
              style={{
                textAlign: "center",
                color: "#7f8c8d",
                fontSize: "0.8rem",
                marginTop: "0.5rem",
              }}
            >
              {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""}{" "}
              found
            </div>
          </div>

          {/* Job Listings */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {filteredJobs.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem 1rem",
                  color: "#7f8c8d",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔍</div>
                <h3 style={{ marginBottom: "0.5rem" }}>No jobs found</h3>
                <p style={{ fontSize: "0.9rem" }}>
                  Try adjusting your search criteria
                </p>
              </div>
            ) : (
              <div>
                {filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    style={{
                      padding: "1.25rem",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                      backgroundColor:
                        selectedJob?.id === job.id ? "#f8f9fa" : "white",
                      borderLeft:
                        selectedJob?.id === job.id
                          ? "3px solid #007bff"
                          : "3px solid transparent",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => handleJobSelect(job)}
                    onMouseEnter={(e) => {
                      if (selectedJob?.id !== job.id) {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedJob?.id !== job.id) {
                        e.currentTarget.style.backgroundColor = "white";
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "0.75rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3
                          style={{
                            margin: "0 0 0.25rem 0",
                            color: "#2c3e50",
                            fontSize: "1.1rem",
                            fontWeight: "600",
                            lineHeight: "1.3",
                          }}
                        >
                          {job.title}
                        </h3>
                        <div
                          style={{
                            color: "#007bff",
                            fontWeight: "500",
                            fontSize: "0.95rem",
                            marginBottom: "0.25rem",
                          }}
                        >
                          {job.company}
                        </div>
                        <div
                          style={{
                            color: "#7f8c8d",
                            fontSize: "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <span>📍 {job.location}</span>
                          <span>•</span>
                          <span>{formatDate(job.posted_date)}</span>
                        </div>
                      </div>
                      {user && (
                        <button
                          type="button"
                          onClick={(e) => handleToggleSave(job, e)}
                          disabled={saveJobBusyId === job.id}
                          title={
                            savedJobIds[job.id]
                              ? "Remove from saved jobs"
                              : "Save job for later"
                          }
                          aria-label={
                            savedJobIds[job.id]
                              ? "Remove from saved jobs"
                              : "Save job for later"
                          }
                          style={{
                            flexShrink: 0,
                            background: savedJobIds[job.id]
                              ? "#fff0f0"
                              : "white",
                            border: `1px solid ${
                              savedJobIds[job.id] ? "#f1aeb5" : "#dee2e6"
                            }`,
                            borderRadius: "8px",
                            padding: "0.35rem 0.5rem",
                            cursor:
                              saveJobBusyId === job.id ? "wait" : "pointer",
                            fontSize: "1.15rem",
                            lineHeight: 1,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              color: savedJobIds[job.id]
                                ? "#e03131"
                                : "#adb5bd",
                            }}
                          >
                            {savedJobIds[job.id] ? "♥" : "♡"}
                          </span>
                        </button>
                      )}
                    </div>

                    {/* Key Details */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.4rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: "#e8f5e8",
                          color: "#27ae60",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          fontWeight: "500",
                        }}
                      >
                        💼{" "}
                        {job.job_type
                          ?.replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                          "Full Time"}
                      </span>
                    </div>

                    {/* Salary */}
                    <div
                      style={{
                        color: "#27ae60",
                        fontWeight: "600",
                        fontSize: "0.85rem",
                      }}
                    >
                      {formatSalary(
                        job.salary_min,
                        job.salary_max,
                        job.salary_currency,
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Job Details */}
        <div
          style={{
            width: windowWidth < 768 ? "100%" : "60%",
            height: windowWidth < 768 ? "60%" : "100%",
            backgroundColor: "white",
            overflowY: "auto",
            display: windowWidth < 768 && !selectedJob ? "none" : "flex",
            flexDirection: "column",
            transition: "all 0.3s ease",
          }}
        >
          {selectedJob ? (
            <>
              {/* Job Header */}
              <div
                style={{
                  padding: "2rem",
                  borderBottom: "1px solid #e9ecef",
                  backgroundColor: "white",
                }}
              >
                {/* Mobile Back Button */}
                {windowWidth < 768 && (
                  <button
                    onClick={() => setSelectedJob(null)}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "0.5rem 1rem",
                      cursor: "pointer",
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.9rem",
                      color: "#666",
                    }}
                  >
                    ← Back to Jobs
                  </button>
                )}
                <div style={{ marginBottom: "1rem" }}>
                  <h1
                    style={{
                      margin: "0 0 0.5rem 0",
                      color: "#2c3e50",
                      fontSize: "1.8rem",
                      fontWeight: "600",
                      lineHeight: "1.2",
                    }}
                  >
                    {selectedJob.title}
                  </h1>
                  <div
                    style={{
                      color: "#007bff",
                      fontWeight: "600",
                      fontSize: "1.2rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {selectedJob.company}
                  </div>
                  <div
                    style={{
                      color: "#7f8c8d",
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>📍 {selectedJob.location}</span>
                    <span>•</span>
                    <span>📅 Posted {formatDate(selectedJob.posted_date)}</span>
                    <span>•</span>
                    <span
                      style={{
                        backgroundColor: "#e8f5e8",
                        color: "#27ae60",
                        padding: "0.25rem 0.75rem",
                        borderRadius: "15px",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                      }}
                    >
                      {selectedJob.job_type
                        ?.replace("_", " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                        "Full Time"}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: "1rem",
                    borderTop: "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      color: "#27ae60",
                      fontWeight: "600",
                      fontSize: "1.1rem",
                    }}
                  >
                    {formatSalary(
                      selectedJob.salary_min,
                      selectedJob.salary_max,
                      selectedJob.salary_currency,
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      alignItems: "center",
                    }}
                  >
                    {user && (
                      <button
                        type="button"
                        onClick={(e) => handleToggleSave(selectedJob, e)}
                        disabled={saveJobBusyId === selectedJob.id}
                        title={
                          savedJobIds[selectedJob.id]
                            ? "Remove from saved jobs"
                            : "Save job for later"
                        }
                        aria-label={
                          savedJobIds[selectedJob.id]
                            ? "Remove from saved jobs"
                            : "Save job for later"
                        }
                        style={{
                          flexShrink: 0,
                          background: savedJobIds[selectedJob.id]
                            ? "#fff0f0"
                            : "white",
                          border: `1px solid ${
                            savedJobIds[selectedJob.id] ? "#f1aeb5" : "#dee2e6"
                          }`,
                          borderRadius: "8px",
                          padding: "0.65rem 0.85rem",
                          cursor:
                            saveJobBusyId === selectedJob.id
                              ? "wait"
                              : "pointer",
                          fontSize: "1.2rem",
                          lineHeight: 1,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            color: savedJobIds[selectedJob.id]
                              ? "#e03131"
                              : "#adb5bd",
                          }}
                        >
                          {savedJobIds[selectedJob.id] ? "♥" : "♡"}
                        </span>
                      </button>
                    )}
                    {user && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: matchCache[selectedJob.id]
                              ? "#5a32a3"
                              : "#6f42c1",
                            color: "white",
                            border: "none",
                            padding: "0.875rem 1.5rem",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "1rem",
                            transition: "background-color 0.2s",
                          }}
                          onClick={() => handleAnalyzeMatch(selectedJob)}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#4a2893";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = matchCache[
                              selectedJob.id
                            ]
                              ? "#5a32a3"
                              : "#6f42c1";
                          }}
                        >
                          {matchCache[selectedJob.id] ? (
                            <>
                              <IconImage
                                name="chart"
                                size={16}
                                alt="Chart"
                                style={{ marginRight: "6px" }}
                              />{" "}
                              View Report
                            </>
                          ) : (
                            <>
                              <IconImage
                                name="robot"
                                size={16}
                                alt="Robot"
                                style={{ marginRight: "6px" }}
                              />{" "}
                              Analyze Match
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    {matchCache[selectedJob.id] && (
                      <div
                        style={{
                          backgroundColor: getMatchColor(
                            matchCache[selectedJob.id].matchRate,
                          ),
                          color: "white",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "20px",
                          fontWeight: "700",
                          fontSize: "0.9rem",
                          minWidth: "50px",
                          textAlign: "center",
                          order: -1,
                        }}
                      >
                        {matchCache[selectedJob.id].matchRate}%
                      </div>
                    )}
                    <button
                      style={{
                        backgroundColor: !user
                          ? "#6c757d"
                          : isLimitReached
                            ? "#dc3545"
                            : "#007bff",
                        color: "white",
                        border: "none",
                        padding: "0.875rem 2rem",
                        borderRadius: "6px",
                        cursor:
                          user && !isLimitReached ? "pointer" : "not-allowed",
                        fontWeight: "600",
                        fontSize: "1rem",
                        transition: "background-color 0.2s",
                        position: "relative",
                      }}
                      onClick={(e) => handleApplyClick(selectedJob, e)}
                      onMouseEnter={(e) => {
                        if (user && !isLimitReached) {
                          e.target.style.backgroundColor = "#0056b3";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (user && !isLimitReached) {
                          e.target.style.backgroundColor = "#007bff";
                        }
                      }}
                      title={
                        !user
                          ? "Sign in to apply"
                          : isLimitReached
                            ? `Daily limit reached (${applicationLimit.applications_today}/${DAILY_APPLICATION_CAP} applications)`
                            : "Apply to this job"
                      }
                    >
                      {!user
                        ? "Sign In to Apply"
                        : isLimitReached
                          ? `Limit Reached (${applicationLimit.applications_today}/${DAILY_APPLICATION_CAP})`
                          : "Apply Now"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Job Details */}
              <div style={{ padding: "2rem", flex: 1 }}>
                {/* AI Summary Section */}
                {loadingSummary && !jobSummaryCache[selectedJob.id] ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#7f8c8d",
                    }}
                  >
                    <div
                      style={{
                        width: "30px",
                        height: "30px",
                        border: "3px solid #e9ecef",
                        borderTopColor: "#007bff",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto 1rem",
                      }}
                    />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <p>Generating summary...</p>
                  </div>
                ) : jobSummaryCache[selectedJob.id] ? (
                  <>
                    {/* AI Formatted Job Posting */}
                    {jobSummaryCache[selectedJob.id].overview && (
                      <div style={{ marginBottom: "1.5rem" }}>
                        <h3
                          style={{
                            color: "#2c3e50",
                            fontSize: "1.1rem",
                            fontWeight: "600",
                            marginBottom: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          📝 Overview
                        </h3>
                        <p
                          style={{
                            color: "#555",
                            lineHeight: "1.7",
                            margin: 0,
                          }}
                        >
                          {jobSummaryCache[selectedJob.id].overview}
                        </p>
                      </div>
                    )}

                    {jobSummaryCache[selectedJob.id].sections?.map(
                      (section, idx) => (
                        <div key={idx} style={{ marginBottom: "1.5rem" }}>
                          <h3
                            style={{
                              color: "#2c3e50",
                              fontSize: "1.1rem",
                              fontWeight: "600",
                              marginBottom: "0.75rem",
                            }}
                          >
                            {section.heading}
                          </h3>
                          {section.paragraphs?.length > 0 && (
                            <div style={{ marginBottom: "0.75rem" }}>
                              {section.paragraphs.map((p, i) => (
                                <p
                                  key={i}
                                  style={{
                                    color: "#555",
                                    lineHeight: "1.7",
                                    margin: "0 0 0.5rem 0",
                                  }}
                                >
                                  {p}
                                </p>
                              ))}
                            </div>
                          )}
                          {section.bullets?.length > 0 && (
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: "1.25rem",
                                color: "#555",
                              }}
                            >
                              {section.bullets.map((b, i) => (
                                <li
                                  key={i}
                                  style={{
                                    marginBottom: "0.5rem",
                                    lineHeight: "1.6",
                                  }}
                                >
                                  {b}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ),
                    )}
                  </>
                ) : (
                  /* Fallback to original description if AI fails */
                  <div style={{ marginBottom: "2rem" }}>
                    <h3
                      style={{
                        color: "#2c3e50",
                        fontSize: "1.2rem",
                        fontWeight: "600",
                        marginBottom: "1rem",
                      }}
                    >
                      Job Description
                    </h3>
                    <div
                      style={{
                        color: "#2c3e50",
                        lineHeight: "1.6",
                        fontSize: "1rem",
                      }}
                    >
                      {selectedJob.description}
                    </div>
                  </div>
                )}

                {!user && (
                  <div
                    style={{
                      backgroundColor: "#e3f2fd",
                      border: "1px solid #bbdefb",
                      borderRadius: "6px",
                      padding: "1.5rem",
                      marginTop: "2rem",
                    }}
                  >
                    <h4
                      style={{
                        color: "#1976d2",
                        margin: "0 0 0.5rem 0",
                        fontSize: "1rem",
                      }}
                    >
                      🔒 Sign in required to apply
                    </h4>
                    <p
                      style={{
                        color: "#1976d2",
                        margin: "0 0 1rem 0",
                        fontSize: "0.9rem",
                      }}
                    >
                      Create an account or sign in to apply for this position
                      and track your applications.
                    </p>
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <button
                        onClick={() => navigate("/sign-up")}
                        style={{
                          backgroundColor: "#007bff",
                          color: "white",
                          border: "none",
                          padding: "0.75rem 1.5rem",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "500",
                          fontSize: "0.9rem",
                        }}
                      >
                        Create Account
                      </button>
                      <button
                        onClick={() => navigate("/sign-in")}
                        style={{
                          backgroundColor: "transparent",
                          color: "#007bff",
                          border: "1px solid #007bff",
                          padding: "0.75rem 1.5rem",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "500",
                          fontSize: "0.9rem",
                        }}
                      >
                        Sign In
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#7f8c8d",
                textAlign: "center",
                padding: "2rem",
              }}
            >
              <div>
                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>👆</div>
                <h3 style={{ marginBottom: "0.5rem" }}>
                  Select a job to view details here
                </h3>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Application Confirmation Modal */}
      {showApplicationModal && pendingJobApplication && (
        <ApplicationConfirmationModal
          job={pendingJobApplication}
          onConfirm={handleApplicationConfirm}
          onCancel={handleApplicationCancel}
        />
      )}

      {showDailyLimitCelebration && (
        <DailyLimitCelebrationModal
          dailyCap={DAILY_APPLICATION_CAP}
          onClose={() => setShowDailyLimitCelebration(false)}
        />
      )}

      {/* Match Analysis Modal */}
      {showMatchModal && (
        <MatchAnalysisModal
          analysis={matchAnalysis}
          jobTitle={selectedJob?.title}
          onClose={closeMatchModal}
          isLoading={matchLoading}
          error={matchError}
        />
      )}
    </div>
  );
}

export default JobList;
